export const ATTRIBUTION_STORAGE_KEY = "mindtale_attribution_v1" as const;
export const ATTRIBUTION_LAST_TOUCH_KEY =
  "mindtale_attribution_last_v1" as const;

export type AcquisitionType =
  | "paid_google"
  | "paid_meta"
  | "paid_other"
  | "organic_search"
  | "referral"
  | "direct";

export type AttributionQuery = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  ttclid: string | null;
};

export type Attribution = {
  version: 1;
  first_seen_at: string;
  landing_url: string;
  landing_path: string;
  referrer: string | null;
  acquisition_type: AcquisitionType;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  ttclid: string | null;
  source_detail: {
    search_engine: string | null;
    referrer_host: string | null;
  };
};

const paidMediums = new Set(["cpc", "ppc", "paid", "paid_social"]);

const searchEngines: Record<string, string[]> = {
  google: ["google."],
  bing: ["bing."],
  yahoo: ["yahoo."],
  duckduckgo: ["duckduckgo."],
  yandex: ["yandex."],
  ecosia: ["ecosia."],
};

const normalizeParam = (value: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getParam = (params: URLSearchParams, key: string) =>
  normalizeParam(params.get(key));

const getReferrerHost = (referrer: string | null) => {
  if (!referrer) return null;
  try {
    return new URL(referrer).host.toLowerCase();
  } catch {
    return null;
  }
};

const getSearchEngine = (referrerHost: string | null) => {
  if (!referrerHost) return null;
  for (const [engine, fragments] of Object.entries(searchEngines)) {
    if (fragments.some((fragment) => referrerHost.includes(fragment))) {
      return engine;
    }
  }
  return null;
};

export const readQueryAttribution = (urlSearch: string): AttributionQuery => {
  const params = new URLSearchParams(urlSearch);
  return {
    utm_source: getParam(params, "utm_source"),
    utm_medium: getParam(params, "utm_medium"),
    utm_campaign: getParam(params, "utm_campaign"),
    utm_content: getParam(params, "utm_content"),
    utm_term: getParam(params, "utm_term"),
    gclid: getParam(params, "gclid"),
    gbraid: getParam(params, "gbraid"),
    wbraid: getParam(params, "wbraid"),
    fbclid: getParam(params, "fbclid"),
    msclkid: getParam(params, "msclkid"),
    ttclid: getParam(params, "ttclid"),
  };
};

export const classifyAcquisition = (
  queryData: AttributionQuery,
  referrer: string | null
): AcquisitionType => {
  if (queryData.gclid || queryData.gbraid || queryData.wbraid) {
    return "paid_google";
  }

  if (queryData.fbclid) {
    return "paid_meta";
  }

  const utmMedium = queryData.utm_medium?.toLowerCase() ?? null;
  if (
    queryData.msclkid ||
    queryData.ttclid ||
    (utmMedium && paidMediums.has(utmMedium))
  ) {
    return "paid_other";
  }

  const referrerHost = getReferrerHost(referrer);
  const searchEngine = getSearchEngine(referrerHost);
  if (searchEngine) {
    return "organic_search";
  }

  if (referrerHost) {
    return "referral";
  }

  return "direct";
};

export const buildAttributionPayload = (
  location: Location,
  referrer: string | null
): Attribution => {
  const queryData = readQueryAttribution(location.search);
  const referrerValue = normalizeParam(referrer);
  const referrerHost = getReferrerHost(referrerValue);
  const searchEngine = getSearchEngine(referrerHost);

  return {
    version: 1,
    first_seen_at: new Date().toISOString(),
    landing_url: location.href,
    landing_path: location.pathname,
    referrer: referrerValue,
    acquisition_type: classifyAcquisition(queryData, referrerValue),
    utm_source: queryData.utm_source,
    utm_medium: queryData.utm_medium,
    utm_campaign: queryData.utm_campaign,
    utm_content: queryData.utm_content,
    utm_term: queryData.utm_term,
    gclid: queryData.gclid,
    gbraid: queryData.gbraid,
    wbraid: queryData.wbraid,
    fbclid: queryData.fbclid,
    msclkid: queryData.msclkid,
    ttclid: queryData.ttclid,
    source_detail: {
      search_engine: searchEngine,
      referrer_host: referrerHost,
    },
  };
};

export const storeFirstTouchAttribution = (payload: Attribution): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY)) {
      return false;
    }
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(payload)
    );
    return true;
  } catch {
    return false;
  }
};

const hasPaidSignals = (payload: Attribution): boolean =>
  payload.acquisition_type === "paid_google" ||
  payload.acquisition_type === "paid_meta" ||
  payload.acquisition_type === "paid_other" ||
  !!payload.fbclid ||
  !!payload.gclid ||
  !!payload.gbraid ||
  !!payload.wbraid ||
  !!payload.msclkid ||
  !!payload.ttclid;

export const storeLastTouchAttribution = (payload: Attribution): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (hasPaidSignals(payload)) {
      // New paid click — full overwrite
      window.localStorage.setItem(
        ATTRIBUTION_LAST_TOUCH_KEY,
        JSON.stringify(payload)
      );
      return true;
    }

    // No paid signals — preserve existing attribution, only update current page
    const raw = window.localStorage.getItem(ATTRIBUTION_LAST_TOUCH_KEY);
    if (raw) {
      const existing = JSON.parse(raw) as Attribution;
      window.localStorage.setItem(
        ATTRIBUTION_LAST_TOUCH_KEY,
        JSON.stringify({
          ...existing,
          landing_path: payload.landing_path,
          landing_url: payload.landing_url,
        })
      );
    } else {
      window.localStorage.setItem(
        ATTRIBUTION_LAST_TOUCH_KEY,
        JSON.stringify(payload)
      );
    }
    return true;
  } catch {
    return false;
  }
};

export const getStoredFirstTouchAttribution = (): Attribution | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
};
